import addDays from 'date-fns/addDays'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import endOfDay from 'date-fns/endOfDay'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import min from 'date-fns/min'
import max from 'date-fns/max'
import { useFormikContext } from 'formik'
import { isEqual } from 'lodash'
import { ChangeEvent, ReactElement, useMemo, useRef, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { CalendarModal, getDaysIntervalForEvents } from 'components/CalendarModal'
import { IEvent } from 'components/CalendarModal/types'
import { AddCircleIcon } from 'components/Icons/AddCircleIcon'
import { BusOutlinedIcon } from 'components/Icons/BusOutlinedIcon'
import { CalendarIcon } from 'components/Icons/CalendarIcon'
import { CancelIcon } from 'components/Icons/CancelIcon'
import { TableColumnsIcon } from 'components/Icons/TableColumnsIcon'
import { VehicleIcon } from 'components/Icons/VehicleIcon'
import { Checkbox } from 'components/v2/Checkbox'
import Dropdown from 'components/v2/Dropdown'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import IconButton from 'components/v2/IconButton'
import { InputDate } from 'components/v2/InputDate'
import { Label } from 'components/v2/Label'
import { DowSelector } from 'components/v2/DowSelector'
import { SelectFrequency } from 'features/Services/components/NewServicesV2/components/SelectFrequency'
import { VehicleAutocomplete } from 'features/Vehicles/components/VehicleAutocomplete'
import { GTTVehicleAutocomplete, GTVehicleSuggestion } from 'gql/types.generated'
import {
  ALL_OPERATION_DAYS,
  DEFAULT_FREQUENCY,
  getEmptyVehicle,
  getFrequencyDaysCount,
  operationDaysMatchDate
} from 'models/services'
import { formatISODate } from 'utils/date'
import { verifyFormik } from 'utils/form'
import { safeFormatMessage } from 'utils/intl'
import { EVehicleScheduleType, IVehicle, IVehicleFrequency, IVehiclesCardProps } from './types'
import { IService, IServicesForm } from '../ServicesForm/types'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { DriverAutocomplete } from 'features/Drivers/components/DriverAutocomplete'
import { IDriverAutocomplete } from 'features/Drivers/components/MultiDriverAutocomplete'
import { useIntersectionObserver } from 'hooks/intersection-observer'

const messages = defineMessages({
  vehiclePlaceholder: {
    id: 'services.form.vehicle.placeholder',
    defaultMessage: 'Select a vehicle'
  },
  driverPlaceholder: {
    id: 'services.form.driver.placeholder',
    defaultMessage: 'Select a driver'
  }
})

const VehicleSettingsContainer = styled.div<TSFixMe>`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DropdownContent = styled(Dropdown.Content)`
  border-radius: 8px;
  padding: 16px;
  width: 200px;
`

const CheckboxWrapper = styled.div`
  display: grid;
  gap: 8px 0;
`

const StyledTh = styled(Th)`
  border: 1px solid #b0b0b0;
  padding: 14px;

  & > div {
    margin: 0;
  }
`

const VehicleTh = styled(StyledTh)`
  width: 300px;
`

const DateTh = styled(StyledTh)`
  width: 180px;
`

const OperationDaysTh = styled(StyledTh)`
  width: 270px;
`

const FrequencyTh = styled(StyledTh)`
  width: 180px;
`

const DriverTh = styled(StyledTh)`
  width: 180px;
`

const VehicleRow = styled(Tr)<{ isVisible: boolean }>`
  &:nth-child(2n) {
    background-color: transparent;
  }
`

const VehicleColumnPlaceholder = styled.td`
  height: 50px;
`

const VehicleTd = styled(Td)<{ disabled?: boolean; padding?: boolean }>`
  border: 1px solid #b0b0b0;
  line-height: 1;
  padding: ${({ padding }): string => (padding ? '8px' : '0')};
  background-color: ${({ disabled }): string => (disabled ? '#F8F8F8' : 'transparent')};
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'inherit')};
`

const ActionTh = styled(Th)`
  border: none;
`

const ActionTd = styled(Td)`
  line-height: 1;
  padding: 0px;
  vertical-align: middle;
`

const DeleteVehicleAction = styled.div`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin-left: 10px;

  svg {
    height: 20px;
    width: 20px;
  }
`

const ErrorWrapper = styled.div`
  padding: 0 12px;
`

const DowWrapper = styled.div`
  padding: 7px 12px;
`

enum EOptionalColumns {
  OperationDays = 'operationDays',
  Frequency = 'frequency'
}

const optionalColumnsMessages = defineMessages({
  [EOptionalColumns.OperationDays]: {
    id: 'services.form.vehicle.operationDays.label',
    defaultMessage: 'Operation Days'
  },
  [EOptionalColumns.Frequency]: {
    id: 'services.form.vehicle.frequency.label',
    defaultMessage: 'Frequency'
  }
})

const OPTINAL_COLUMNS_LIST = [EOptionalColumns.OperationDays, EOptionalColumns.Frequency]

const getVehicleEventsInterval = (vehicle: IVehicle, service: IService, calendarDaysInterval: Date[]): [Date, Date] => {
  const firstCalendarDate = calendarDaysInterval.at(0) as Date
  const lastCalendarDate = calendarDaysInterval.at(-1) as Date
  const potentialStartDateArray = [firstCalendarDate]
  if (service.startDate) {
    potentialStartDateArray.push(service.startDate)
  }
  if (vehicle.startDate) {
    potentialStartDateArray.push(vehicle.startDate)
  }
  const startDate = max(potentialStartDateArray)
  const potentialEndDateArray = [lastCalendarDate]
  if (vehicle.expirationDate) {
    potentialEndDateArray.push(vehicle.expirationDate)
  }
  if (service.endDate) {
    potentialEndDateArray.push(service.endDate)
  }
  const endDate = endOfDay(min(potentialEndDateArray))
  return [startDate, endDate]
}

const getFirstVehicleOccurrenceDate = (vehicle: IVehicle, startDate: Date): Date => {
  if (vehicle.startDate && isAfter(vehicle.startDate, startDate)) {
    return vehicle.startDate
  }
  const frequencyDays = getFrequencyDaysCount(vehicle.frequency)
  const daysDifference = vehicle.startDate ? differenceInCalendarDays(startDate, vehicle.startDate) : 0
  const isOccurringOnStartDate = daysDifference % frequencyDays === 0
  if (isOccurringOnStartDate) {
    return startDate
  }
  const missingDays = frequencyDays - (daysDifference % frequencyDays)
  return addDays(startDate, missingDays)
}

const getEventsByDate = (vehicle: IVehicle, date: Date, eventsMap: Record<string, IEvent[]>): IEvent[] => {
  const formattedDate = formatISODate(date)
  const event = {
    name: vehicle.vehicle.name ?? '',
    icon: <BusOutlinedIcon />
  }
  if (!eventsMap[formattedDate]) {
    return [event]
  } else {
    return [...eventsMap[formattedDate], event]
  }
}

const VehicleRowComponent = ({
  vehicle,
  index,
  currentServiceIndex,
  vehicles,
  companyId,
  formDisabled = false,
  visibleColumns
}: {
  vehicle: IVehicle
  index: number
  currentServiceIndex: number
  vehicles: IVehicle[]
  companyId: string
  formDisabled?: boolean
  visibleColumns: EOptionalColumns[]
}): ReactElement | null => {
  const intl = useIntl()
  const elementRef = useRef<HTMLElement>(null)
  const isVisible = useIntersectionObserver(elementRef)
  const { values, setFieldValue, setFieldTouched, handleBlur, touched, errors } = useFormikContext<IServicesForm>()

  const currentService = values.services[currentServiceIndex]
  const currentVehicleString = `services[${currentServiceIndex}].vehicles[${index}]`
  const currentVehicleArray = ['services', `${currentServiceIndex}`, 'vehicles', `${index}`]
  const isFirstVehicle = index === 0
  const isLastVehicle = index === vehicles.length - 1
  const startDateError = verifyFormik<string>(errors, [...currentVehicleArray, 'startDate'])
  const expirationDateError = verifyFormik<string>(errors, [...currentVehicleArray, 'expirationDate'])
  const idError = verifyFormik<string>(errors, [...currentVehicleArray, 'vehicle', 'id'])
  const handleFrequencyChange = async (frequency: IVehicleFrequency): Promise<void> => {
    await setFieldValue(`${currentVehicleString}.frequency`, frequency)
    setFieldTouched(`${currentVehicleString}.frequency`)
  }
  const handleDriverChange = async (driver: IDriverAutocomplete): Promise<void> => {
    await setFieldValue(`${currentVehicleString}.driver`, driver)
    setFieldTouched(`${currentVehicleString}.driver`)
  }
  const today = new Date()
  const defaultMonth = vehicle.startDate ? max([vehicle.startDate, today]) : today
  const disabledDays = {
    before: currentService.startDate ? max([currentService.startDate, today]) : today,
    after: currentService.endDate || undefined
  }
  const isSingleVehicle = vehicles.length === 1

  const handleDeleteVehicle = (deletedVehicleIndex: number): void => {
    const lastVehicle = vehicles.at(-1)
    let newVehicles = vehicles.filter((_, index) => index !== deletedVehicleIndex)
    newVehicles = newVehicles.map((vehicle, index) => {
      let expirationDate = vehicle.expirationDate
      let operationDays = vehicle.operationDays
      let frequency = vehicle.frequency
      if (index === newVehicles.length - 1) {
        expirationDate = lastVehicle?.expirationDate || undefined
      }
      if (newVehicles.length === 1) {
        operationDays = ALL_OPERATION_DAYS
        frequency = DEFAULT_FREQUENCY
      }
      return {
        ...vehicle,
        expirationDate,
        operationDays,
        frequency
      }
    })
    setFieldValue(`services[${currentServiceIndex}].vehicles`, newVehicles)
  }

  return (
    <VehicleRow ref={elementRef} isVisible={isVisible}>
      {isVisible ? (
        <>
          <VehicleTd>
            <VehicleAutocomplete
              companyId={companyId}
              id={`${currentVehicleString}.vehicle.id`}
              name={`${currentVehicleString}.vehicle.id`}
              placeholder={intl.formatMessage(messages.vehiclePlaceholder)}
              iconLeft={<VehicleIcon />}
              value={getVehicleLabelWithExternalId(vehicle?.vehicle as GTTVehicleAutocomplete)}
              onChange={(vehicle: GTVehicleSuggestion): void => {
                setFieldValue(`${currentVehicleString}.vehicle`, {
                  id: vehicle.id,
                  name: vehicle.name,
                  externalID: vehicle.externalID
                })
              }}
              touched={verifyFormik<boolean>(touched, [...currentVehicleArray, 'vehicle', 'id'])}
              errors={idError ? <ErrorWrapper>{idError}</ErrorWrapper> : undefined}
              noBorder
              disabled={formDisabled}
              isActive
            />
          </VehicleTd>
          <VehicleTd disabled={isFirstVehicle}>
            <InputDate
              intl={intl}
              id={`${currentVehicleString}.startDate`}
              name={`${currentVehicleString}.startDate`}
              placeholder="--/--/----"
              value={vehicle?.startDate}
              onChange={async (value): Promise<void> => {
                await setFieldValue(`${currentVehicleString}.startDate`, value)

                if (vehicle.expirationDate && isBefore(vehicle.expirationDate, value as Date)) {
                  setFieldValue(`${currentVehicleString}.expirationDate`, value)
                }
              }}
              disabledDays={disabledDays}
              touched
              errors={startDateError ? <ErrorWrapper>{startDateError}</ErrorWrapper> : undefined}
              noBorder
              disabled={isFirstVehicle || formDisabled}
              defaultMonth={defaultMonth}
            />
          </VehicleTd>
          <VehicleTd disabled={isLastVehicle}>
            <InputDate
              intl={intl}
              id={`${currentVehicleString}.expirationDate`}
              name={`${currentVehicleString}.expirationDate`}
              placeholder="--/--/----"
              value={vehicle?.expirationDate}
              onChange={async (value): Promise<void> => {
                await setFieldValue(`${currentVehicleString}.expirationDate`, value)

                if (vehicle.startDate && isBefore(value as Date, vehicle.startDate)) {
                  setFieldValue(`${currentVehicleString}.startDate`, value)
                }
              }}
              disabledDays={disabledDays}
              touched
              errors={expirationDateError ? <ErrorWrapper>{expirationDateError}</ErrorWrapper> : undefined}
              noBorder
              disabled={isLastVehicle || formDisabled}
            />
          </VehicleTd>
          {visibleColumns.includes(EOptionalColumns.OperationDays) && (
            <VehicleTd disabled={isSingleVehicle}>
              <DowWrapper>
                <DowSelector
                  id={`${currentVehicleString}.operationDays`}
                  values={vehicle.operationDays}
                  onChange={(values: number[]): void => {
                    setFieldValue(`${currentVehicleString}.operationDays`, values)
                  }}
                  touched={verifyFormik<boolean>(touched, [...currentVehicleArray, 'operationDays'])}
                  errors={verifyFormik<string>(errors, [...currentVehicleArray, 'operationDays'])}
                  disabled={isSingleVehicle || formDisabled}
                />
              </DowWrapper>
            </VehicleTd>
          )}
          {visibleColumns.includes(EOptionalColumns.Frequency) && (
            <VehicleTd disabled={isSingleVehicle} padding>
              <SelectFrequency
                id={`${currentVehicleString}.frequency`}
                frequency={vehicle.frequency}
                onChange={(value: IVehicleFrequency): void => {
                  handleFrequencyChange(value)
                }}
                onBlur={handleBlur}
                disabled={isSingleVehicle || formDisabled}
                touched={Boolean(touched.services?.[currentServiceIndex]?.vehicles?.[index]?.frequency)}
                error={verifyFormik<string>(errors, [...currentVehicleArray, 'frequency'])}
              />
            </VehicleTd>
          )}
          <VehicleTd>
            <DriverAutocomplete
              id={`${currentVehicleString}.driver.id`}
              placeholder={intl.formatMessage(messages.driverPlaceholder)}
              onChange={handleDriverChange}
              value={vehicle.driver.name}
              disabled={formDisabled}
              noBorder
              isActive
            />
          </VehicleTd>
          <ActionTd>
            {!isSingleVehicle && !formDisabled && (
              <DeleteVehicleAction onClick={(): void => handleDeleteVehicle(index)}>
                <CancelIcon />
              </DeleteVehicleAction>
            )}
          </ActionTd>
        </>
      ) : (
        <VehicleColumnPlaceholder />
      )}
    </VehicleRow>
  )
}

export const VehiclesCard = ({ companyId, currentServiceIndex, formDisabled }: IVehiclesCardProps): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue } = useFormikContext<IServicesForm>()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isShowingVehiclesCalendar, setIsShowingVehiclesCalendar] = useState(false)

  const currentService = values.services[currentServiceIndex]
  const { vehicles } = currentService

  const getInitialVisibleColumns = (): EOptionalColumns[] => {
    const columns: EOptionalColumns[] = []
    const isShowingOperationDaysColumn = vehicles.some(
      (vehicle) => vehicle.operationDays && !isEqual(vehicle.operationDays, ALL_OPERATION_DAYS)
    )
    const isShowingFrequencyColumn = vehicles.some(
      (vehicle) =>
        vehicle.frequency?.unit !== DEFAULT_FREQUENCY.unit || vehicle.frequency?.value !== DEFAULT_FREQUENCY.value
    )

    if (isShowingOperationDaysColumn) {
      columns.push(EOptionalColumns.OperationDays)
    }
    if (isShowingFrequencyColumn && !isShowingOperationDaysColumn) {
      columns.push(EOptionalColumns.Frequency)
    }
    return columns
  }
  const [visibleColumns, setVisibleColumns] = useState<EOptionalColumns[]>(getInitialVisibleColumns())

  const handleSelectColumn = (event: ChangeEvent<HTMLInputElement>): void => {
    const id = event.target.id as EOptionalColumns
    if (visibleColumns.includes(id)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== id))
      if (id === EOptionalColumns.OperationDays) {
        setFieldValue(
          `services[${currentServiceIndex}].vehicles`,
          vehicles.map((vehicle) => ({ ...vehicle, operationDays: ALL_OPERATION_DAYS }))
        )
      }
      if (id === EOptionalColumns.Frequency) {
        setFieldValue(
          `services[${currentServiceIndex}].vehicles`,
          vehicles.map((vehicle) => ({ ...vehicle, type: EVehicleScheduleType.Dow, frequency: DEFAULT_FREQUENCY }))
        )
      }
    } else {
      if (id === EOptionalColumns.Frequency) {
        setFieldValue(
          `services[${currentServiceIndex}].vehicles`,
          vehicles.map((vehicle) => ({ ...vehicle, type: EVehicleScheduleType.Frequency }))
        )
      }

      setVisibleColumns([...visibleColumns, id])
    }
  }

  const handleAddVehicle = (): void => {
    const { startDate, endDate } = currentService
    const newVehicle = getEmptyVehicle(startDate, endDate)
    if (visibleColumns.includes(EOptionalColumns.Frequency)) {
      newVehicle.type = EVehicleScheduleType.Frequency
    }
    setFieldValue(`services[${currentServiceIndex}].vehicles`, [...vehicles, newVehicle])
  }

  const eventsMap: Record<string, IEvent[]> = useMemo(() => {
    const calendarDaysInterval = getDaysIntervalForEvents()
    const eventsMap: Record<string, IEvent[]> = {}
    vehicles.forEach((vehicle) => {
      const [startDate, endDate] = getVehicleEventsInterval(vehicle, currentService, calendarDaysInterval)

      if (vehicle.type === EVehicleScheduleType.Frequency) {
        const frequencyInDays = getFrequencyDaysCount(vehicle.frequency)
        let date = getFirstVehicleOccurrenceDate(vehicle, startDate)
        while (isBefore(date, endDate)) {
          const formattedDate = formatISODate(date)
          eventsMap[formattedDate] = getEventsByDate(vehicle, date, eventsMap)
          date = addDays(date, frequencyInDays)
        }
      } else {
        let date = startDate
        while (isBefore(date, endDate)) {
          if (operationDaysMatchDate(vehicle.operationDays, date)) {
            const formattedDate = formatISODate(date)
            eventsMap[formattedDate] = getEventsByDate(vehicle, date, eventsMap)
          }
          date = addDays(date, 1)
        }
      }
    })
    return eventsMap
  }, [vehicles, currentService])

  return (
    <>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="services.form.section.vehicles.title" defaultMessage="Vehicle Settings" />
          </Heading.H4>
          <Actions>
            <IconButton icon={<AddCircleIcon />} onClick={handleAddVehicle} disabled={formDisabled} />
            <IconButton icon={<CalendarIcon />} onClick={(): void => setIsShowingVehiclesCalendar(true)} />
          </Actions>
        </FormLayout.CardHeader>
        <VehicleSettingsContainer>
          <Dropdown.Wrapper onClickAway={(): void => setIsMenuOpen(false)} form>
            <IconButton
              icon={<TableColumnsIcon />}
              onClick={(): void => setIsMenuOpen(!isMenuOpen)}
              disabled={formDisabled}
            />
            <DropdownContent open={isMenuOpen} noIndicator left form>
              <CheckboxWrapper>
                {OPTINAL_COLUMNS_LIST.map((column): ReactElement => {
                  const isDisabled =
                    (column === EOptionalColumns.Frequency &&
                      visibleColumns.includes(EOptionalColumns.OperationDays)) ||
                    (column === EOptionalColumns.OperationDays && visibleColumns.includes(EOptionalColumns.Frequency))
                  return (
                    <Checkbox
                      key={`checkbox-${column}`}
                      id={column}
                      checked={visibleColumns.includes(column)}
                      onChange={handleSelectColumn}
                      disabled={isDisabled}
                    >
                      {safeFormatMessage({ intl, key: column, messages: optionalColumnsMessages })}
                    </Checkbox>
                  )
                })}
              </CheckboxWrapper>
            </DropdownContent>
          </Dropdown.Wrapper>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <VehicleTh>
                    <Label>
                      <FormattedMessage id="services.form.vehicle.column.name" defaultMessage="Vehicle" />
                    </Label>
                  </VehicleTh>
                  <DateTh>
                    <Label>
                      <FormattedMessage id="services.form.vehicle.column.startAt" defaultMessage="Start At" />
                    </Label>
                  </DateTh>
                  <DateTh>
                    <Label>
                      <FormattedMessage
                        id="services.form.vehicle.column.expirationDate"
                        defaultMessage="Expiration date"
                      />
                    </Label>
                  </DateTh>
                  {visibleColumns.includes(EOptionalColumns.OperationDays) && (
                    <OperationDaysTh>
                      <Label>
                        <FormattedMessage
                          id="services.form.vehicle.column.operationDays"
                          defaultMessage="Operation Days"
                        />
                      </Label>
                    </OperationDaysTh>
                  )}
                  {visibleColumns.includes(EOptionalColumns.Frequency) && (
                    <FrequencyTh>
                      <Label>
                        <FormattedMessage id="services.form.vehicle.column.frequency" defaultMessage="Frequency" />
                      </Label>
                    </FrequencyTh>
                  )}
                  <DriverTh>
                    <Label>
                      <FormattedMessage id="services.form.vehicle.column.driver" defaultMessage="Driver" />
                    </Label>
                  </DriverTh>
                  <ActionTh />
                </Tr>
              </Thead>
              <Tbody>
                {vehicles.map((vehicle, index): ReactElement => {
                  return (
                    <VehicleRowComponent
                      key={index}
                      index={index}
                      vehicle={vehicle}
                      currentServiceIndex={currentServiceIndex}
                      companyId={companyId}
                      vehicles={vehicles}
                      formDisabled={formDisabled}
                      visibleColumns={visibleColumns}
                    />
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </VehicleSettingsContainer>
      </FormLayout.Card>
      {isShowingVehiclesCalendar && (
        <CalendarModal
          onClose={(): void => setIsShowingVehiclesCalendar(false)}
          title={<FormattedMessage id="vehicles.calendar.title" defaultMessage="Vehicles" />}
          eventsMap={eventsMap}
        />
      )}
    </>
  )
}
